import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AdminNavbar, Breadcrumbs } from '../components/AdminNavbar';
import api from '../utils/api';
import SecureImage from '../components/SecureImage';
import { toast } from 'sonner';
import {
  ArrowLeft, User, Mail, Phone, CreditCard, Calendar, Clock,
  Shield, CheckCircle, XCircle, FileText, DollarSign,
  AlertTriangle, Loader2, Image as ImageIcon, Ban, Truck, RefreshCw
} from 'lucide-react';
import { resolveEscrowUiState, resolveSmartDealFinancialPresentation } from '../components/transactionState';
import { safeExternalHttpUrl } from '../utils/transactionSharing';
import {
  resolveTransactionPlatformFee,
  resolveTransactionTotal,
  usePlatformConfig,
} from '../context/PlatformConfigContext';

// TrustTrade Color System
const COLORS = {
  primary: '#1a2942',
  green: '#2ecc71',
  background: '#ffffff',
  section: '#f8f9fa',
  text: '#212529',
  subtext: '#6c757d',
  border: '#dee2e6',
  error: '#e74c3c',
  warning: '#f39c12',
  info: '#3498db'
};

// Known ShipLogic (Courier Guy) service levels for manual admin booking overrides.
// Mirrors SERVICE_LEVEL_CODE_TO_ID in backend/services/courier_guy.py.
const COURIER_SERVICE_LEVELS = [
  { code: 'LSF', id: 212572 },
  { code: 'LOF', id: 212573 },
  { code: 'ECO', id: 212570 },
  { code: 'LSE', id: 212574 },
  { code: 'LSX', id: 212575 },
];

function AdminTransactionDetail() {
  const { config: platformConfig } = usePlatformConfig();
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [buyer, setBuyer] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [adminNote, setAdminNote] = useState('');
  // Admin-selected service level for a manual courier booking ('' = use stored).
  const [courierServiceLevelId, setCourierServiceLevelId] = useState('');

  // Confirmation modals
  const [confirmModal, setConfirmModal] = useState({ open: false, action: '', title: '', message: '' });

  const platformFeePercent = transaction?.platform_fee_percent
    ?? transaction?.trusttrade_fee_percent
    ?? platformConfig.platform_fee_percent;
  const platformFee = transaction
    ? resolveTransactionPlatformFee(transaction, platformFeePercent)
    : 0;
  const transactionTotal = transaction
    ? resolveTransactionTotal(transaction, platformFeePercent)
    : 0;

  useEffect(() => {
    // Smart Deal records (SD-*) are not regular escrow transactions — they live in
    // the Smart Deals collection/view. Redirect to the Smart Deal detail page
    // (stripping any -M{n} milestone-child suffix to the parent deal) instead of
    // calling the transaction endpoint, which would 404 with "Transaction not found".
    if (transactionId && transactionId.startsWith('SD-')) {
      const parentDealId = transactionId.replace(/-M\d+$/, '');
      navigate(`/smart-deals/${parentDealId}`, { replace: true });
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId]);

  const fetchData = async () => {
    try {
      const [userRes, txnRes] = await Promise.all([
        api.get('/auth/me'),
        api.get(`/admin/transaction/${transactionId}`)
      ]);
      
      if (!userRes.data.is_admin) {
        toast.error('Admin access required');
        navigate('/dashboard');
        return;
      }
      
      setUser(userRes.data);
      setTransaction(txnRes.data.transaction);
      setBuyer(txnRes.data.buyer);
      setSeller(txnRes.data.seller);
    } catch (error) {
      console.error('Failed to fetch:', error);
      toast.error('Failed to load transaction details');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const openConfirmModal = (action, title, message) => {
    setConfirmModal({ open: true, action, title, message });
  };

  // Force-release a single Smart Deal milestone's escrow when the buyer's approve
  // failed. Shows the EXACT TradeSafe error returned by the backend, right here.
  const forceReleaseMilestone = async (milestoneId, seq) => {
    const dealId = transaction.deal_id || transaction.transaction_id;
    if (!window.confirm(`Force-release Stage ${seq} (${milestoneId}) for ${dealId}? Funds will be sent to the seller.`)) return;
    setActionLoading(true);
    try {
      await api.post(`/smart-deals/${dealId}/milestones/${milestoneId}/admin-release`, {});
      toast.success(`Stage ${seq} released`);
      await fetchData();
    } catch (error) {
      // backend returns "Release failed: <exact TradeSafe reason>"
      toast.error(error.response?.data?.detail || 'Release failed', { duration: 10000 });
    } finally {
      setActionLoading(false);
    }
  };

  // Manually run the payment-verification fallback for this transaction. Same path
  // the background job runs every few minutes — queries TradeSafe directly and, if
  // funds have cleared (FUNDS_DEPOSITED), advances the txn to Payment Secured, sends
  // notifications and books the courier. Use this for transactions stuck in
  // "Awaiting Payment" after a card payment whose webhook never reached us.
  const verifyPayment = async () => {
    setActionLoading(true);
    try {
      const res = await api.post(`/admin/transactions/${transactionId}/verify-payment`, {});
      const result = res.data?.result || {};
      if (result.updated) {
        toast.success(`Payment verified — ${result.action || 'transaction updated'}`, { duration: 8000 });
      } else {
        const ts = result.ts_state ? ` (TradeSafe state: ${result.ts_state})` : '';
        toast(`No payment detected yet${ts}. ${result.reason || ''}`, { duration: 8000 });
      }
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed', { duration: 8000 });
    } finally {
      setActionLoading(false);
    }
  };

  // Release a stuck courier-booking claim so booking can be retried. Used when a
  // previous attempt failed (no waybill) and the transaction is being skipped with
  // "booking already claimed/done".
  const resetCourierBooking = async () => {
    setActionLoading(true);
    try {
      const res = await api.post(`/admin/transactions/${transactionId}/reset-courier-booking`, {});
      if (res.data?.reset) {
        toast.success('Courier booking claim reset — you can book again now', { duration: 7000 });
      } else {
        toast(res.data?.reason === 'already booked' ? 'Already booked — nothing to reset' : 'Nothing to reset', { duration: 6000 });
      }
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed', { duration: 7000 });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAction = async () => {
    const action = confirmModal.action;
    setConfirmModal({ ...confirmModal, open: false });
    setActionLoading(true);
    
    try {
      let endpoint = '';
      let data = { admin_email: user?.email, admin_note: adminNote };
      
      switch (action) {
        case 'release_funds':
          endpoint = `/admin/transactions/${transactionId}/release`;
          data.notes = adminNote;
          break;
        case 'refund_buyer':
          endpoint = `/admin/transactions/${transactionId}/refund`;
          data.reason = adminNote || 'Admin refund';
          break;
        case 'suspend_buyer':
          endpoint = `/admin/users/${buyer?.user_id}/suspend`;
          break;
        case 'suspend_seller':
          endpoint = `/admin/users/${seller?.user_id}/suspend`;
          break;
        case 'add_note':
          endpoint = `/admin/transactions/${transactionId}/notes`;
          data = { notes: adminNote };
          break;
        case 'cancel_transaction':
          endpoint = `/admin/transactions/${transactionId}/cancel`;
          data.reason = adminNote || 'Cancelled by admin';
          break;
        case 'force_start_delivery':
          endpoint = `/tradesafe/start-delivery/${transactionId}`;
          break;
        case 'force_mark_delivered':
          endpoint = `/tradesafe/manual-accept-delivery/${transactionId}`;
          break;
        case 'force_release_escrow':
          endpoint = `/tradesafe/accept-delivery/${transactionId}`;
          break;
        case 'force_cancel':
          endpoint = `/tradesafe/cancel/${transactionId}`;
          break;
        case 'book_courier':
          endpoint = `/admin/transactions/${transactionId}/book-courier`;
          data = courierServiceLevelId ? { service_level_id: Number(courierServiceLevelId) } : {};
          break;
        default:
          toast.error('Unknown action');
          return;
      }
      
      await api.post(endpoint, data);
      toast.success(`Action completed: ${action.replace('_', ' ')}`);
      setAdminNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      // Always refetch — even after a failure — so the panel (e.g. the courier
      // "Last error") reflects the latest attempt instead of a stale value.
      fetchData();
      setActionLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout', {});
      localStorage.removeItem('session_token');
      navigate('/');
    } catch (error) {
      localStorage.removeItem('session_token');
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COLORS.section }}>
        <div className="w-12 h-12 border-4 rounded-full animate-spin" style={{ borderColor: COLORS.primary, borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: COLORS.section }}>
        <AdminNavbar user={user} onLogout={handleLogout} />
        <div className="flex-1 flex items-center justify-center">
          <p style={{ color: COLORS.text }}>Transaction not found</p>
        </div>
      </div>
    );
  }

  const financialState = resolveEscrowUiState(transaction);

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.section }}>
      <AdminNavbar user={user} onLogout={handleLogout} />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumbs */}
        <Breadcrumbs items={[
          { label: 'Admin', href: '/admin' },
          { label: 'Transactions', href: '/admin?tab=transactions' },
          { label: transaction.share_code || transactionId }
        ]} />

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin')} 
              className="p-2"
              data-testid="back-to-admin"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: COLORS.text }} data-testid="transaction-title">
                Transaction {transaction.share_code}
              </h1>
              <p style={{ color: COLORS.subtext }} className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Created {new Date(transaction.created_at).toLocaleString()}
              </p>
            </div>
          </div>
          <Badge style={{ backgroundColor: financialState.color, color: 'white' }} className="text-sm px-4 py-2" data-testid="transaction-status">
            {financialState.label}
          </Badge>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - Left 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Transaction Overview */}
            <Card className="p-6" style={{ backgroundColor: COLORS.background }} data-testid="overview-section">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: COLORS.primary }}>
                <FileText className="w-5 h-5" /> Transaction Overview
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Transaction ID</p>
                  <p className="font-mono text-sm" style={{ color: COLORS.text }}>{transaction.transaction_id}</p>
                </div>
                <div>
                  <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Reference Code</p>
                  <p className="font-mono text-sm font-semibold" style={{ color: COLORS.primary }}>{transaction.share_code}</p>
                </div>
                <div>
                  <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Created</p>
                  <p className="text-sm" style={{ color: COLORS.text }}>{new Date(transaction.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Delivery Method</p>
                  <p className="text-sm capitalize" style={{ color: COLORS.text }}>{transaction.delivery_method?.replace(/_/g, ' ') || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Status</p>
                  <Badge style={{ backgroundColor: financialState.color, color: 'white' }}>{financialState.label}</Badge>
                </div>
                {transaction.tradesafe_id && (
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>TradeSafe Reference</p>
                    <p className="font-mono text-sm" style={{ color: COLORS.text }}>{transaction.tradesafe_id}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Two columns: Buyer and Seller */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Buyer Details */}
              <Card className="p-6" style={{ backgroundColor: COLORS.background }} data-testid="buyer-section">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: COLORS.primary }}>
                  <User className="w-5 h-5" /> Buyer Details
                </h2>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Full Name</p>
                    <p className="text-sm font-medium" style={{ color: COLORS.text }}>{transaction.buyer_name}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Email</p>
                    <p className="text-sm flex items-center gap-1" style={{ color: COLORS.text }}>
                      <Mail className="w-3 h-3" /> {transaction.buyer_email}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Phone</p>
                    <p className="text-sm flex items-center gap-1" style={{ color: COLORS.text }}>
                      <Phone className="w-3 h-3" /> {buyer?.phone || transaction.buyer_phone || 'Not provided'}
                    </p>
                  </div>
                  {buyer?.id_number && (
                    <div>
                      <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>ID Number</p>
                      <p className="text-sm font-mono" style={{ color: COLORS.text }}>{buyer.id_number}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Verified Status</p>
                    {buyer?.id_verified || buyer?.verified ? (
                      <Badge style={{ backgroundColor: COLORS.green, color: 'white' }} className="inline-flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Verified
                      </Badge>
                    ) : (
                      <Badge style={{ backgroundColor: COLORS.subtext, color: 'white' }}>Not Verified</Badge>
                    )}
                  </div>
                  
                  {/* Buyer ID Photo */}
                  {(buyer?.id_front_path || buyer?.id_document || buyer?.verification?.id_document_path) && (
                    <div>
                      <p className="text-xs uppercase mb-2 font-medium" style={{ color: COLORS.subtext }}>ID Document</p>
                      <div className="relative group">
                        <SecureImage
                          reference={buyer.id_front_path || buyer.id_document || buyer.verification?.id_document_path}
                          category="verification"
                          alt="Buyer ID" 
                          className="h-32 object-contain cursor-pointer rounded border hover:opacity-90"
                          style={{ borderColor: COLORS.border }}
                          downloadName="trusttrade-buyer-id"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Seller Details */}
              <Card className="p-6" style={{ backgroundColor: COLORS.background }} data-testid="seller-section">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: COLORS.primary }}>
                  <User className="w-5 h-5" /> Seller Details
                </h2>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Full Name</p>
                    <p className="text-sm font-medium" style={{ color: COLORS.text }}>{transaction.seller_name}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Email</p>
                    <p className="text-sm flex items-center gap-1" style={{ color: COLORS.text }}>
                      <Mail className="w-3 h-3" /> {transaction.seller_email}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Phone</p>
                    <p className="text-sm flex items-center gap-1" style={{ color: COLORS.text }}>
                      <Phone className="w-3 h-3" /> {seller?.phone || transaction.seller_phone || 'Not provided'}
                    </p>
                  </div>
                  {seller?.id_number && (
                    <div>
                      <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>ID Number</p>
                      <p className="text-sm font-mono" style={{ color: COLORS.text }}>{seller.id_number}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Banking Details</p>
                    {seller?.banking_details_added ? (
                      <Badge style={{ backgroundColor: COLORS.green, color: 'white' }} className="inline-flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Added
                      </Badge>
                    ) : (
                      <Badge style={{ backgroundColor: COLORS.warning, color: 'white' }}>Not Added</Badge>
                    )}
                  </div>
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Verified Status</p>
                    {seller?.id_verified || seller?.verified ? (
                      <Badge style={{ backgroundColor: COLORS.green, color: 'white' }} className="inline-flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Verified
                      </Badge>
                    ) : (
                      <Badge style={{ backgroundColor: COLORS.subtext, color: 'white' }}>Not Verified</Badge>
                    )}
                  </div>
                  
                  {/* Seller ID Photo */}
                  {(seller?.id_front_path || seller?.id_document || seller?.verification?.id_document_path) && (
                    <div>
                      <p className="text-xs uppercase mb-2 font-medium" style={{ color: COLORS.subtext }}>ID Document</p>
                      <div className="relative group">
                        <SecureImage
                          reference={seller.id_front_path || seller.id_document || seller.verification?.id_document_path}
                          category="verification"
                          alt="Seller ID" 
                          className="h-32 object-contain cursor-pointer rounded border hover:opacity-90"
                          style={{ borderColor: COLORS.border }}
                          downloadName="trusttrade-seller-id"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Item Details */}
            <Card className="p-6" style={{ backgroundColor: COLORS.background }} data-testid="item-section">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: COLORS.primary }}>
                <ImageIcon className="w-5 h-5" /> Item Details
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Description</p>
                  <p className="text-sm" style={{ color: COLORS.text }}>{transaction.item_description}</p>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Category</p>
                    <p className="text-sm capitalize" style={{ color: COLORS.text }}>{transaction.item_category || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Condition</p>
                    <p className="text-sm" style={{ color: COLORS.text }}>{transaction.item_condition || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Known Issues</p>
                    <p className="text-sm" style={{ color: COLORS.text }}>{transaction.known_issues || 'None reported'}</p>
                  </div>
                </div>
                
                {/* Item Photos Gallery */}
                {transaction.item_photos && transaction.item_photos.length > 0 && (
                  <div>
                    <p className="text-xs uppercase mb-3 font-medium" style={{ color: COLORS.subtext }}>
                      Photos ({transaction.item_photos.length})
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {transaction.item_photos.map((photo, i) => (
                        <div key={i} className="relative group">
                          <SecureImage
                            reference={photo}
                            category="photos"
                            alt={`Item ${i+1}`} 
                            className="w-full h-32 object-cover rounded cursor-pointer border hover:opacity-90 transition-opacity"
                            style={{ borderColor: COLORS.border }}
                            downloadName={`item-photo-${i+1}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Payment Details */}
            <Card className="p-6" style={{ backgroundColor: COLORS.background }} data-testid="payment-section">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: COLORS.primary }}>
                <CreditCard className="w-5 h-5" /> Payment Details
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Item Price</p>
                  <p className="text-xl font-bold" style={{ color: COLORS.text }}>R {transaction.item_price?.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <div>
                  <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>TrustTrade Fee ({Number(platformFeePercent).toLocaleString()}%)</p>
                  <p className="text-sm" style={{ color: COLORS.text }}>R {platformFee.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <div>
                  <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Total Amount</p>
                  <p className="text-xl font-bold" style={{ color: COLORS.green }}>
                    R {transactionTotal.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Fee Paid By</p>
                  <p className="text-sm capitalize" style={{ color: COLORS.text }}>{transaction.fee_paid_by || 'Buyer'}</p>
                </div>
                {transaction.payment_date && (
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Payment Date</p>
                    <p className="text-sm" style={{ color: COLORS.text }}>{new Date(transaction.payment_date).toLocaleString()}</p>
                  </div>
                )}
                {transaction.payment_method && (
                  <div>
                    <p className="text-xs uppercase mb-1 font-medium" style={{ color: COLORS.subtext }}>Payment Method</p>
                    <p className="text-sm capitalize" style={{ color: COLORS.text }}>{transaction.payment_method}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Transaction Timeline */}
            <Card className="p-6" style={{ backgroundColor: COLORS.background }} data-testid="timeline-section">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: COLORS.primary }}>
                <Clock className="w-5 h-5" /> Transaction Timeline
              </h2>
              <div className="space-y-4">
                {/* Default events */}
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.green }}></div>
                    <div className="w-0.5 flex-1" style={{ backgroundColor: COLORS.border }}></div>
                  </div>
                  <div className="pb-4">
                    <p className="text-sm font-medium" style={{ color: COLORS.text }}>Transaction Created</p>
                    <p className="text-xs" style={{ color: COLORS.subtext }}>{new Date(transaction.created_at).toLocaleString()}</p>
                    <p className="text-xs" style={{ color: COLORS.subtext }}>By: {transaction.creator_email || 'System'}</p>
                  </div>
                </div>
                
                {transaction.seller_confirmed && (
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.green }}></div>
                      <div className="w-0.5 flex-1" style={{ backgroundColor: COLORS.border }}></div>
                    </div>
                    <div className="pb-4">
                      <p className="text-sm font-medium" style={{ color: COLORS.text }}>Seller Confirmed</p>
                      <p className="text-xs" style={{ color: COLORS.subtext }}>{transaction.seller_confirmed_at ? new Date(transaction.seller_confirmed_at).toLocaleString() : 'Date not recorded'}</p>
                    </div>
                  </div>
                )}
                
                {transaction.tradesafe_id && (
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.info }}></div>
                      <div className="w-0.5 flex-1" style={{ backgroundColor: COLORS.border }}></div>
                    </div>
                    <div className="pb-4">
                      <p className="text-sm font-medium" style={{ color: COLORS.text }}>Escrow Created on TradeSafe</p>
                      <p className="text-xs" style={{ color: COLORS.subtext }}>Reference: {transaction.tradesafe_id}</p>
                    </div>
                  </div>
                )}
                
                {transaction.payment_status === 'Paid' && (
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.green }}></div>
                      <div className="w-0.5 flex-1" style={{ backgroundColor: COLORS.border }}></div>
                    </div>
                    <div className="pb-4">
                      <p className="text-sm font-medium" style={{ color: COLORS.text }}>Payment Received</p>
                      <p className="text-xs" style={{ color: COLORS.subtext }}>Funds now held in escrow</p>
                    </div>
                  </div>
                )}
                
                {/* Custom timeline events */}
                {transaction.timeline && transaction.timeline.map((event, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.primary }}></div>
                      <div className="w-0.5 flex-1" style={{ backgroundColor: COLORS.border }}></div>
                    </div>
                    <div className="pb-4">
                      <p className="text-sm font-medium" style={{ color: COLORS.text }}>{event.action || event.status}</p>
                      <p className="text-xs" style={{ color: COLORS.subtext }}>
                        {new Date(event.timestamp || event.created_at).toLocaleString()}
                        {event.by && ` • ${event.by}`}
                      </p>
                      {event.note && <p className="text-xs mt-1" style={{ color: COLORS.text }}>{event.note}</p>}
                    </div>
                  </div>
                ))}
                
                {/* Admin notes in timeline */}
                {transaction.admin_notes && transaction.admin_notes.map((note, i) => (
                  <div key={`note-${i}`} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.warning }}></div>
                      <div className="w-0.5 flex-1" style={{ backgroundColor: COLORS.border }}></div>
                    </div>
                    <div className="pb-4">
                      <p className="text-sm font-medium" style={{ color: COLORS.text }}>Admin Note</p>
                      <p className="text-xs" style={{ color: COLORS.subtext }}>
                        {note.timestamp ? new Date(note.timestamp).toLocaleString() : 'Date not recorded'}
                        {note.admin_email && ` • ${note.admin_email}`}
                      </p>
                      <p className="text-sm mt-1 p-2 rounded" style={{ backgroundColor: COLORS.section, color: COLORS.text }}>
                        {typeof note === 'string' ? note : note.note}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Admin Actions Sidebar - Right column */}
          <div className="space-y-6">
            {/* Admin Actions Panel */}
            <Card className="p-6" style={{ backgroundColor: COLORS.background }} data-testid="admin-actions-panel">
              <h2 className="text-lg font-semibold mb-4" style={{ color: COLORS.primary }}>Admin Actions</h2>
              <div className="space-y-3">
                {/* Verify Payment — recover a transaction whose payment webhook was
                    missed (e.g. card payments stuck in "Awaiting Payment"). Re-queries
                    TradeSafe and advances the txn if funds have cleared. */}
                {['Awaiting Payment', 'Ready for Payment', 'Pending Payment', 'Funds Secured'].includes(transaction.payment_status) && (
                  <Button
                    onClick={verifyPayment}
                    disabled={actionLoading}
                    className="w-full text-white justify-center"
                    style={{ backgroundColor: COLORS.info }}
                    data-testid="verify-payment-btn"
                  >
                    {actionLoading
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <CheckCircle className="w-4 h-4 mr-2" />}
                    Verify Payment
                  </Button>
                )}

                <Button
                  onClick={() => openConfirmModal('release_funds', 'Release Funds', `Release funds to ${transaction.seller_name}? This action cannot be undone.`)}
                  disabled={actionLoading || !['Paid', 'Funds in Escrow'].includes(transaction.payment_status)}
                  className="w-full text-white justify-center"
                  style={{ backgroundColor: COLORS.green }}
                  data-testid="release-funds-btn"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Release Funds to Seller
                </Button>
                
                <Button 
                  onClick={() => openConfirmModal('refund_buyer', 'Refund Buyer', `Refund ${transaction.buyer_name}? This will cancel the transaction and return funds.`)}
                  disabled={actionLoading || !['Paid', 'Ready for Payment', 'Funds in Escrow'].includes(transaction.payment_status)}
                  className="w-full text-white justify-center"
                  style={{ backgroundColor: COLORS.error }}
                  data-testid="refund-buyer-btn"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Refund Buyer
                </Button>
                
                <Button 
                  onClick={() => openConfirmModal('suspend_buyer', 'Suspend Buyer', `Suspend buyer account (${transaction.buyer_name})? They will not be able to use the platform.`)}
                  disabled={actionLoading}
                  className="w-full text-white justify-center"
                  style={{ backgroundColor: COLORS.warning }}
                  data-testid="suspend-buyer-btn"
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Suspend Buyer
                </Button>
                
                <Button
                  onClick={() => openConfirmModal('suspend_seller', 'Suspend Seller', `Suspend seller account (${transaction.seller_name})? They will not be able to use the platform.`)}
                  disabled={actionLoading}
                  className="w-full text-white justify-center"
                  style={{ backgroundColor: COLORS.warning }}
                  data-testid="suspend-seller-btn"
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Suspend Seller
                </Button>

                {['CREATED', 'PENDING_CONFIRMATION', 'AWAITING_PAYMENT'].includes(transaction.transaction_state) && !transaction.archived && (
                  <>
                    <div style={{ borderTop: `1px solid ${COLORS.border}`, marginTop: 4 }} />
                    <Button
                      onClick={() => openConfirmModal('cancel_transaction', 'Cancel Transaction', `Cancel transaction ${transaction.share_code}? This will archive the transaction and cannot be undone.`)}
                      disabled={actionLoading}
                      className="w-full text-white justify-center"
                      style={{ backgroundColor: COLORS.error }}
                      data-testid="cancel-transaction-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancel Transaction
                    </Button>
                  </>
                )}
              </div>
              
              {/* Admin Notes Section */}
              <div className="mt-6 pt-6" style={{ borderTop: `1px solid ${COLORS.border}` }}>
                <p className="text-sm font-medium mb-2" style={{ color: COLORS.text }}>Add Admin Note</p>
                <Textarea 
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="Add a note about this transaction or action..."
                  rows={3}
                  className="mb-3"
                  style={{ borderColor: COLORS.border }}
                  data-testid="admin-note-input"
                />
                <Button 
                  onClick={() => openConfirmModal('add_note', 'Add Note', 'Save this admin note to the transaction timeline?')}
                  disabled={actionLoading || !adminNote.trim()}
                  className="w-full text-white justify-center"
                  style={{ backgroundColor: COLORS.primary }}
                  data-testid="save-note-btn"
                >
                  Save Note
                </Button>
              </div>
            </Card>

            {/* Admin Override Controls */}
            <Card className="p-6" style={{ backgroundColor: COLORS.background, border: `2px solid ${COLORS.error}` }} data-testid="admin-override-panel">
              <h2 className="text-lg font-semibold mb-1 flex items-center gap-2" style={{ color: COLORS.error }}>
                <AlertTriangle className="w-5 h-5" /> Admin Override Controls
              </h2>
              <p className="text-xs mb-4" style={{ color: COLORS.subtext }}>Force state changes — bypasses normal validation. Use with caution.</p>
              <div className="space-y-3">
                <Button
                  onClick={() => openConfirmModal('force_start_delivery', 'Force: Mark Delivery Started', `Force-move transaction ${transaction.share_code} to delivery state? This bypasses seller action and notifies the buyer.`)}
                  disabled={actionLoading}
                  className="w-full text-white justify-center"
                  style={{ backgroundColor: '#7c3aed' }}
                  data-testid="force-start-delivery-btn"
                >
                  <Truck className="w-4 h-4 mr-2" />
                  Force: Mark Delivery Started
                </Button>

                <Button
                  onClick={() => openConfirmModal('force_mark_delivered', 'Force: Mark Delivered', `Force-mark transaction ${transaction.share_code} as delivered? This triggers payout processing.`)}
                  disabled={actionLoading}
                  className="w-full text-white justify-center"
                  style={{ backgroundColor: '#0891b2' }}
                  data-testid="force-mark-delivered-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Force: Mark Delivered
                </Button>

                {/* Force-release escrow is hidden for courier deliveries — those
                    auto-release when the buyer confirms receipt or the timer expires,
                    and forcing it via allocationAcceptDelivery is rejected by TradeSafe. */}
                {transaction.delivery_method === 'courier' ? (
                  <p className="text-xs p-3 rounded" style={{ backgroundColor: COLORS.section, color: COLORS.subtext }}>
                    Courier transactions auto-release when the buyer confirms receipt or the
                    delivery timer expires. Force release isn't available for courier deliveries.
                  </p>
                ) : (
                  <Button
                    onClick={() => openConfirmModal('force_release_escrow', 'Force: Release Escrow', `Force-release escrow for transaction ${transaction.share_code}? Funds will be sent to the seller immediately.`)}
                    disabled={actionLoading}
                    className="w-full text-white justify-center"
                    style={{ backgroundColor: COLORS.green }}
                    data-testid="force-release-escrow-btn"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Force: Release Escrow
                  </Button>
                )}

                <Button
                  onClick={() => openConfirmModal('force_cancel', 'Force: Cancel Transaction', `Force-cancel transaction ${transaction.share_code}? This archives the transaction from any state and cannot be undone.`)}
                  disabled={actionLoading}
                  className="w-full text-white justify-center"
                  style={{ backgroundColor: COLORS.error }}
                  data-testid="force-cancel-btn"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Force: Cancel Transaction
                </Button>
              </div>
            </Card>

            {/* Smart Deal milestones — per-stage force release when an approve fails */}
            {transaction.deal_type === 'DIGITAL_WORK_MILESTONE' && Array.isArray(transaction.milestones) && (
              <Card className="p-6" style={{ backgroundColor: COLORS.background }} data-testid="milestone-release-panel">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: COLORS.primary }}>
                  <DollarSign className="w-4 h-4" /> Milestone Force-Release
                </h3>
                <p className="text-xs mb-3" style={{ color: COLORS.subtext }}>
                  Use this if a buyer's "Approve" failed. The exact TradeSafe reason is shown if it fails again.
                </p>
                <div className="space-y-2">
                  {[...transaction.milestones].sort((a, b) => (a.seq || 0) - (b.seq || 0)).map((ms) => {
                    const released = ms.status === 'RELEASED';
                    const financial = resolveSmartDealFinancialPresentation(ms);
                    return (
                      <div key={ms.milestone_id} className="flex items-center justify-between gap-3 p-2 rounded" style={{ backgroundColor: COLORS.section }}>
                        <div className="text-sm min-w-0">
                          <span className="font-medium" style={{ color: COLORS.text }}>Stage {ms.seq}</span>
                          <span className="ml-2" style={{ color: COLORS.subtext }}>{financial ? financial.label : ms.status}</span>
                          {ms.payout_error && (
                            <span className="ml-2 text-xs" style={{ color: COLORS.error }}>· {ms.payout_error}</span>
                          )}
                        </div>
                        <Button
                          onClick={() => forceReleaseMilestone(ms.milestone_id, ms.seq)}
                          disabled={actionLoading || released}
                          className="text-white justify-center shrink-0"
                          style={{ backgroundColor: released ? COLORS.border : COLORS.green, opacity: released ? 0.6 : 1 }}
                          data-testid={`force-release-milestone-${ms.milestone_id}`}
                        >
                          {released ? (financial?.label || 'Payout pending confirmation') : 'Force Release'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Courier Booking — only for courier deliveries */}
            {transaction.delivery_method === 'courier' && (
              <Card className="p-6" style={{ backgroundColor: COLORS.background }} data-testid="courier-panel">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: COLORS.primary }}>
                  <Truck className="w-4 h-4" /> Courier
                </h3>
                <div className="space-y-3 text-sm">
                  {transaction.courier_waybill ? (
                    <>
                      <div className="flex justify-between items-center">
                        <span style={{ color: COLORS.subtext }}>Waybill</span>
                        <span className="font-mono" style={{ color: COLORS.text }}>{transaction.courier_waybill}</span>
                      </div>
                      {safeExternalHttpUrl(transaction.courier_tracking_url) && (
                        <a
                          href={safeExternalHttpUrl(transaction.courier_tracking_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm underline"
                          style={{ color: COLORS.info }}
                        >
                          Track shipment
                        </a>
                      )}
                      <Badge style={{ backgroundColor: COLORS.green, color: 'white' }} className="inline-flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Booked
                      </Badge>
                    </>
                  ) : (
                    <>
                      <p style={{ color: COLORS.subtext }}>No shipment booked yet.</p>
                      <div className="flex justify-between items-center">
                        <span style={{ color: COLORS.subtext }}>Service level code</span>
                        <span className="font-mono" style={{ color: COLORS.text }}>{transaction.courier_quote_id || '—'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span style={{ color: COLORS.subtext }}>Service level id</span>
                        <span className="font-mono" style={{ color: COLORS.text }}>{transaction.courier_service_level_id ?? '—'}</span>
                      </div>
                      {transaction.courier_booking_error && (
                        <p className="text-xs p-2 rounded" style={{ backgroundColor: COLORS.section, color: COLORS.error }}>
                          Last error: {transaction.courier_booking_error}
                        </p>
                      )}
                      <div>
                        <label className="text-xs uppercase mb-1 font-medium block" style={{ color: COLORS.subtext }}>
                          Service level to book
                        </label>
                        <select
                          value={courierServiceLevelId}
                          onChange={(e) => setCourierServiceLevelId(e.target.value)}
                          className="w-full text-sm p-2 rounded border"
                          style={{ borderColor: COLORS.border, color: COLORS.text, backgroundColor: COLORS.background }}
                          data-testid="courier-service-level-select"
                        >
                          <option value="">Use stored ({transaction.courier_quote_id || 'none'})</option>
                          {COURIER_SERVICE_LEVELS.map((sl) => (
                            <option key={sl.id} value={sl.id}>{sl.code} ({sl.id})</option>
                          ))}
                        </select>
                      </div>
                      <Button
                        onClick={() => openConfirmModal('book_courier', 'Book Courier', `Book the Courier Guy shipment for ${transaction.share_code}${courierServiceLevelId ? ` using service level id ${courierServiceLevelId}` : ` using the stored service level (${transaction.courier_quote_id || 'none'})`}? This requests a waybill and emails both parties the tracking link.`)}
                        disabled={actionLoading}
                        className="w-full text-white justify-center"
                        style={{ backgroundColor: '#7c3aed' }}
                        data-testid="book-courier-btn"
                      >
                        <Truck className="w-4 h-4 mr-2" />
                        Book Courier
                      </Button>

                      {/* Only shown when a previous booking attempt failed/stuck (no
                          waybill yet): releases the atomic claim so booking can retry. */}
                      {(transaction.courier_booking_error || transaction.courier_booking_in_progress) && (
                        <Button
                          onClick={resetCourierBooking}
                          disabled={actionLoading}
                          variant="outline"
                          className="w-full justify-center mt-2"
                          style={{ borderColor: COLORS.warning, color: COLORS.warning }}
                          data-testid="reset-courier-booking-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Reset Courier Booking
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </Card>
            )}

            {/* Quick Info */}
            <Card className="p-6" style={{ backgroundColor: COLORS.background }}>
              <h3 className="text-sm font-semibold mb-3" style={{ color: COLORS.primary }}>Quick Info</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span style={{ color: COLORS.subtext }}>Auto Release</span>
                  <span style={{ color: COLORS.text }}>{transaction.auto_release_days || 3} days</span>
                </div>
                {transaction.auto_release_at && (
                  <div className="flex justify-between items-center">
                    <span style={{ color: COLORS.subtext }}>Release Date</span>
                    <span style={{ color: COLORS.text }}>{new Date(transaction.auto_release_at).toLocaleDateString()}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span style={{ color: COLORS.subtext }}>Has Dispute</span>
                  <span style={{ color: transaction.has_dispute ? COLORS.error : COLORS.text }}>
                    {transaction.has_dispute ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span style={{ color: COLORS.subtext }}>Delivery Confirmed</span>
                  <span style={{ color: COLORS.text }}>{transaction.delivery_confirmed ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </Card>

            {/* Saved Admin Notes */}
            {transaction.admin_notes && transaction.admin_notes.length > 0 && (
              <Card className="p-6" style={{ backgroundColor: COLORS.background }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: COLORS.primary }}>
                  Saved Notes ({transaction.admin_notes.length})
                </h3>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {transaction.admin_notes.map((note, i) => (
                    <div key={i} className="p-3 rounded text-sm" style={{ backgroundColor: COLORS.section }}>
                      <p style={{ color: COLORS.text }}>{typeof note === 'string' ? note : note.note}</p>
                      <p className="text-xs mt-1" style={{ color: COLORS.subtext }}>
                        {note.timestamp ? new Date(note.timestamp).toLocaleString() : ''}
                        {note.admin_email && ` • ${note.admin_email}`}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmModal.open} onOpenChange={(open) => setConfirmModal({ ...confirmModal, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ color: COLORS.text }}>{confirmModal.title}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm" style={{ color: COLORS.subtext }}>{confirmModal.message}</p>
            {confirmModal.action !== 'add_note' && (
              <div className="mt-4 p-3 rounded" style={{ backgroundColor: COLORS.section }}>
                <p className="text-xs mb-2" style={{ color: COLORS.subtext }}>This action will:</p>
                <ul className="text-xs space-y-1" style={{ color: COLORS.text }}>
                  <li>• Log to transaction timeline with your admin email</li>
                  <li>• Send email notification to both parties</li>
                  <li>• Send SMS notification to both parties</li>
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmModal({ ...confirmModal, open: false })}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction} 
              disabled={actionLoading}
              className="text-white"
              style={{
                backgroundColor: confirmModal.action === 'force_cancel' || confirmModal.action.includes('refund') || confirmModal.action.includes('suspend') || confirmModal.action === 'cancel_transaction'
                  ? COLORS.error
                  : confirmModal.action === 'force_release_escrow' || confirmModal.action.includes('release')
                    ? COLORS.green
                    : confirmModal.action === 'force_start_delivery'
                      ? '#7c3aed'
                      : confirmModal.action === 'force_mark_delivered'
                        ? '#0891b2'
                        : COLORS.primary
              }}
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminTransactionDetail;
